import { ColorModeContext, useMode } from "./theme";
import { CssBaseline, ThemeProvider, Box } from "@mui/material";
import { Toaster } from "react-hot-toast";
import { useSelector, useDispatch } from "react-redux";
import { selectCurrentUser } from "./store/user/user.selector";
import Auth from "./routes/auth";
import LoggedApp from "./routes/logged-app";
import "./index.css";
import { useEffect, useMemo, useState } from "react";
import { setUser } from "./store/user/user.action";
import { onAuthStateChangedListener } from "./utils/firebase/firebase";
import HomeSkeletonPage from "./components/home-button/home-skeleton";
import lillyBelleFont from "./assets/fonts/lillyBelleFont";
import { Routes, Route, useLocation } from "react-router-dom";
import PublicPersonalizedPage from "./pages/personalizados/PublicPersonalizedPage.jsx";
import PublicOrderSuccessPage from "./pages/personalizados/PublicOrderSuccessPage.jsx";

function App() {
  const [theme, colorMode] = useMode();
  const user = useSelector(selectCurrentUser);
  const dispatch = useDispatch();
  const location = useLocation();

  const [authChecked, setAuthChecked] = useState(false);

  const ENABLE_PUBLIC_PERSONALIZED_ROUTE = true;

  const publicPaths = useMemo(
    () => [
      "/produtos-personalizados",
      "/produtos-personalizados/pedido-gerado",
    ],
    []
  );

  const isPublicRoute = useMemo(() => {
    return publicPaths.some((path) => location.pathname.startsWith(path));
  }, [location.pathname, publicPaths]);

  useEffect(() => {
    const font = new FontFace(
      "LillyBelle",
      `url(data:font/truetype;charset=utf-8;base64,${lillyBelleFont}) format('truetype')`
    );

    font
      .load()
      .then((loadedFont) => {
        document.fonts.add(loadedFont);
        console.log("Fonte Lilly Belle registrada!");
      })
      .catch((error) => {
        console.error("Erro ao carregar fonte LillyBelle:", error);
      });
  }, []);

  useEffect(() => {
    const unsubscribe = onAuthStateChangedListener((u) => {
      // Anonymous Auth serve apenas para operações públicas,
      // não representa um usuário logado no sistema.
      dispatch(setUser(u?.isAnonymous ? null : u));
      setAuthChecked(true);
    });

    return unsubscribe;
  }, [dispatch]);

  function renderPersonalizedRoute(component) {
    if (ENABLE_PUBLIC_PERSONALIZED_ROUTE) {
      return component;
    }

    if (!authChecked) {
      return <HomeSkeletonPage />;
    }

    return user ? component : <Auth />;
  }

  if (!isPublicRoute && !authChecked) {
    return (
      <Box
        sx={{
          paddingTop: '120px',
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          width: "100vw",
          minHeight: "100vh",
          padding: 0,
          margin: 0,
          backgroundColor: "#902348",
        }}
      >
        <HomeSkeletonPage />
      </Box>
    );
  }

  return (
    <ColorModeContext.Provider value={colorMode}>
      <ThemeProvider theme={theme}>
        <CssBaseline />
        <Toaster position="bottom-right" />

        <div
          className="app"
          style={{
            minHeight: "100vh",
            width: "100%",
            backgroundColor: isPublicRoute ? "#fff" : "transparent",
          }}
        >
          <Routes>
            <Route
              path="/produtos-personalizados"
              element={renderPersonalizedRoute(<PublicPersonalizedPage />)}
            />

            <Route
              path="/produtos-personalizados/pedido-gerado"
              element={renderPersonalizedRoute(<PublicOrderSuccessPage />)}
            />

            <Route path="/*" element={user ? <LoggedApp /> : <Auth />} />
          </Routes>
        </div>
      </ThemeProvider>
    </ColorModeContext.Provider>
  );
}

export default App;